import { Activity, Radio } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { PageHeader } from "@/components/console/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useBridgeActivityHub } from "@/hooks/useBridgeActivityHub";
import { cn } from "@/lib/cn";
import { strings } from "@/lib/strings";
import { wizardRoutePath } from "@/lib/steps";
import {
  BridgeActivityDirection,
  BridgeActivityOutcome,
  type BridgeActivityEntry,
} from "@/types/bridgeActivity";

function directionLabel(d: BridgeActivityDirection): string {
  switch (d) {
    case BridgeActivityDirection.ZitadelToBus:
      return strings.live.direction.zitadelToBus;
    case BridgeActivityDirection.BusToZitadel:
      return strings.live.direction.busToZitadel;
    default:
      return "?";
  }
}

function outcomeClass(o: BridgeActivityOutcome): string {
  switch (o) {
    case BridgeActivityOutcome.Success:
      return "text-success border-success/40";
    case BridgeActivityOutcome.Skipped:
      return "text-muted-foreground border-border/60";
    case BridgeActivityOutcome.Failed:
      return "text-destructive border-destructive/50";
    default:
      return "text-muted-foreground";
  }
}

function outcomeLabel(o: BridgeActivityOutcome): string {
  switch (o) {
    case BridgeActivityOutcome.Success:
      return strings.live.outcome.ok;
    case BridgeActivityOutcome.Skipped:
      return strings.live.outcome.skip;
    case BridgeActivityOutcome.Failed:
      return strings.live.outcome.fail;
    default:
      return "?";
  }
}

function formatLineTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
}

function ConnectionBadge({ state }: { state: string }) {
  const tone =
    state === "connected"
      ? "border-success/50 bg-success/10 text-success"
      : state === "connecting" || state === "reconnecting"
        ? "border-warning/50 bg-warning/10 text-warning"
        : "border-border/60 bg-secondary/40 text-muted-foreground";

  return (
    <Badge variant="outline" className={cn("gap-1.5 font-normal", tone)}>
      <Radio className={cn("size-3", state === "connected" && "animate-pulse")} />
      {strings.live.connection[state as keyof typeof strings.live.connection] ?? state}
    </Badge>
  );
}

export function LiveDashboardPage() {
  const { connectionState, stats, entries, lastError } = useBridgeActivityHub();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [stickToBottom, setStickToBottom] = useState(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !stickToBottom) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [entries.length, stickToBottom]);

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 80;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    setStickToBottom(atBottom);
  }

  const topTypes = stats
    ? Object.entries(stats.byMessageType)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
    : [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <PageHeader
        title={strings.live.title}
        description={strings.live.description}
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to={wizardRoutePath("review")}>{strings.live.openWizard}</Link>
            </Button>
            <ConnectionBadge state={connectionState} />
          </>
        }
      />

      {lastError && connectionState === "disconnected" ? (
        <p className="text-sm text-destructive">{lastError}</p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border/60 bg-card/40">
          <CardHeader className="pb-2">
            <CardDescription>{strings.live.stats.total}</CardDescription>
            <CardTitle className="text-2xl mono-num">{stats?.total ?? "—"}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">{strings.live.stats.instanceNote}</CardContent>
        </Card>
        <Card className="border-border/60 bg-card/40">
          <CardHeader className="pb-2">
            <CardDescription>{strings.live.stats.zitadelToBus}</CardDescription>
            <CardTitle className="text-2xl mono-num">{stats?.zitadelToBus ?? "—"}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-border/60 bg-card/40">
          <CardHeader className="pb-2">
            <CardDescription>{strings.live.stats.busToZitadel}</CardDescription>
            <CardTitle className="text-2xl mono-num">{stats?.busToZitadel ?? "—"}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-border/60 bg-card/40">
          <CardHeader className="pb-2">
            <CardDescription>{strings.live.stats.topTypes}</CardDescription>
          </CardHeader>
          <CardContent className="text-xs leading-relaxed text-muted-foreground">
            {topTypes.length === 0 ? "—" : topTypes.map(([k, v]) => `${k} (${v})`).join(" · ")}
          </CardContent>
        </Card>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col border-border/60 bg-card/30">
        <CardHeader className="flex flex-row items-center gap-2 border-b border-border/60 py-3">
          <Activity className="size-4 text-primary" />
          <CardTitle className="text-base">{strings.live.logTitle}</CardTitle>
        </CardHeader>
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="max-h-[min(70vh,900px)] min-h-[50vh] overflow-y-auto p-3 font-mono text-xs scrollbar-thin"
        >
          {entries.length === 0 ? (
            <p className="text-muted-foreground">{strings.live.logEmpty}</p>
          ) : (
            entries.map((entry: BridgeActivityEntry, index: number) => (
              <div
                key={entry.id}
                className={cn(
                  "flex flex-wrap items-baseline gap-2 rounded px-2 py-1",
                  index === entries.length - 1 && "bg-secondary/40",
                )}
              >
                <span className="w-20 shrink-0 text-[10px] text-muted-foreground mono-num">
                  {formatLineTime(entry.timestampUtc)}
                </span>
                <span className={cn("w-9 shrink-0 font-semibold", outcomeClass(entry.outcome))}>
                  [{outcomeLabel(entry.outcome)}]
                </span>
                <span className="shrink-0 rounded border border-border/60 px-1 text-[10px] uppercase text-muted-foreground">
                  {directionLabel(entry.direction)}
                </span>
                <span className="shrink-0 rounded border border-primary/30 bg-primary/5 px-1 text-[10px] text-primary/90">
                  {entry.messageType}
                </span>
                {entry.correlationId ? (
                  <span className="shrink-0 text-[10px] text-muted-foreground">#{entry.correlationId.slice(0, 8)}…</span>
                ) : null}
                <span className="min-w-0 flex-1 text-foreground/90">{entry.summary}</span>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}
